import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const greeting = 'Ah Homs';

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleUsernameSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) return;

    setLoading(true);
    setError('');

    // Check if username exists in the system
    const allowedUsernames = ['henry', 'omi', 'admin']; // Add more as needed
    const normalizedUsername = username.toLowerCase().trim();

    if (allowedUsernames.includes(normalizedUsername)) {
      setShowPassword(true);
      setLoading(false);
    } else {
      setError(`Hmm... there is no "${username}" living in this home. Do you go by any other names?`);
      setLoading(false);
      // Clear username after a delay
      setTimeout(() => {
        setUsername('');
        setError('');
      }, 6000);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;

    setLoading(true);
    setError('');

    // Convert username to email format for Firebase
    const email = `${username.toLowerCase()}@ldr-webapp.com`;

    const result = await login(email, password);

    if (result.success) {
      // Check if admin user - redirect to admin panel
      if (result.profile?.name?.toLowerCase() === 'admin') {
        navigate('/admin', { replace: true });
      } else {
        // Navigate to homepage with fade transition, passing state to indicate fresh login
        navigate('/home', { replace: true, state: { freshLogin: true } });
      }
    } else {
      setError('That key doesn\'t seem to fit... try another?');
      setLoading(false);
      // Clear password for retry
      setPassword('');
    }
  };

  return (
    <div className="login-page">
      <div className="login-background" />

      <div className="login-container">
        <AnimatePresence mode="wait">
          {!showPassword ? (
            <motion.div
              key="welcome"
              className="login-content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.form
                onSubmit={handleUsernameSubmit}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your name"
                  autoFocus
                  required
                  className="simple-input"
                  disabled={loading}
                />

                {error && (
                  <motion.div
                    className="error-message"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {error}
                  </motion.div>
                )}
              </motion.form>
            </motion.div>
          ) : (
            <motion.div
              key="password"
              className="login-content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                {greeting}
              </motion.h1>

              <motion.form
                onSubmit={handlePasswordSubmit}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
              >
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoFocus
                  required
                  className="simple-input"
                />

                {error && (
                  <motion.div
                    className="error-message"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {error}
                  </motion.div>
                )}
              </motion.form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Login;
